import React, { forwardRef, ForwardRefRenderFunction } from "react";
//#region types
import { EntityDrawerProps, DrawerRightHandles } from "models/propTypes/drawerPropTypes";
import { WintUtilsWebApi } from "wint-api-client/dist/types";
import { ListAPIResponse } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import useMountedState from "../useMountedState";
import useTableContext from "../tables/context/useTableContext";
import DrawerRight from "./drawerRight";
import EntityPagination from "./entityView/entityPagination";
import DrawerEntityView from "./entityView/drawerEntityView";
import LoadingWithLogo from "../loading/loadingWithLogo";
//#endregion

const EntityDrawer: ForwardRefRenderFunction<DrawerRightHandles, EntityDrawerProps> = (props, ref) => {
  const { getMounted } = useMountedState();
  const { entityText, headerText: cardHeader, render, reloadKey, reloadList } = props;
  const { filter } = useTableContext();

  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [total, setTotal] = React.useState<number>();
  const [navigationFilterPage, setNavigationFilterPage] = React.useState<number>();
  const [navigationCurrentIndex, setNavigationCurrentIndex] = React.useState<number>();
  const [indexToOpen, setIndexToOpen] = React.useState<number>();
  const [drawerItems, setDrawerItems] = React.useState<any[]>([]);
  const [currentItem, setCurrentItem] = React.useState<any>();

  const [loading, setLoading] = React.useState<boolean>(false);
  const [filterUpdated, setFilterUpdated] = React.useState<boolean>(false);
  const [listToBeUpdated, setListToBeUpdated] = React.useState<boolean>(false);
  const [filterFuncThroughRef, setFilterFuncThroughRef] =
    React.useState<<T extends WintUtilsWebApi.Filter>(filter: T, key?: number) => ListAPIResponse<any>>();

  const drawerRef = ref as React.RefObject<DrawerRightHandles>;

  React.useEffect(() => {
    if (!isOpen) {
      setFilterUpdated(true);
    }
  }, [filter, reloadKey, props.filter]);

  React.useEffect(() => {
    if (total === 0) {
      drawerRef.current?.closeDrawer();
    }
  }, [total]);

  const onDrawerOpen = (
    toOpenIndex?: number,
    filterFunc?: <T extends WintUtilsWebApi.Filter>(filter: T, key?: number) => ListAPIResponse<any>
  ) => {
    setIsOpen(true);
    if (filterFunc) setTotal(undefined);
    const index = toOpenIndex ?? 0;
    setIndexToOpen(index);
    updateCurrent(index, filter.page, filterFunc);
    setFilterFuncThroughRef(() => filterFunc);
  };

  const onDrawerClose = () => {
    setIsOpen(false);
    if (listToBeUpdated && reloadList) {
      setListToBeUpdated(false);
      reloadList();
    }
  };

  const updateCurrent = (
    index: number,
    page: number,
    filterFunc?: <T extends WintUtilsWebApi.Filter>(filter: T, key?: number) => ListAPIResponse<any>
  ) => {
    if (!loading) {
      if (filterUpdated || navigationFilterPage !== page || drawerItems[index] === undefined || filterFunc) {
        filterUpdated && setFilterUpdated(false);
        setNavigationFilterPage(page);
        reloadItems(index, page, filterFunc);
      } else {
        setCurrentItem(drawerItems[index]);
      }
    }

    setNavigationCurrentIndex(index);
  };

  const reloadItems = (
    index: number,
    page: number,
    filterFunc?: <T extends WintUtilsWebApi.Filter>(filter: T, key?: number) => ListAPIResponse<any>
  ) => {
    const currentFilterFunc = filterFunc ?? filterFuncThroughRef ?? props.filter;
    if (currentFilterFunc) {
      setLoading(true);
      currentFilterFunc!({ ...filter, page: page })
        .then((res) => res.data)
        .then((results) => {
          if (getMounted()) {
            const items = results.items ?? [];
            setDrawerItems(items);
            setCurrentItem(items[index]);
            setTotal(results.totalItems);
          }
        })
        .finally(() => getMounted() && setLoading(false));
    }
  };

  const removeCurrentItem = (id: number | string) => {
    if (
      getMounted() &&
      navigationCurrentIndex !== undefined &&
      ((typeof id === "number" && id === drawerItems[navigationCurrentIndex].id) ||
        (typeof id === "string" && drawerItems[navigationCurrentIndex].guidId
          ? id === drawerItems[navigationCurrentIndex].guidId
          : id === drawerItems[navigationCurrentIndex].id))
    ) {
      const updatedItems = [...drawerItems];
      updatedItems.splice(navigationCurrentIndex, 1);

      const newTotal = (total ?? 0) - 1;
      setDrawerItems(updatedItems);
      setTotal(newTotal);

      if (updatedItems[navigationCurrentIndex]) {
        setCurrentItem(updatedItems[navigationCurrentIndex]);
      } else {
        const currentPage = navigationFilterPage ?? 0;
        const numPerPage = filter.numPerPage ?? 0;
        const currentItemFullIndex = currentPage * numPerPage + navigationCurrentIndex;
        if (currentItemFullIndex === newTotal) {
          if (navigationCurrentIndex !== 0) {
            updateCurrent(navigationCurrentIndex - 1, currentPage);
          } else if (currentPage > 0) {
            reloadItems(numPerPage - 1, currentPage - 1);
          }
        } else {
          reloadItems(navigationCurrentIndex, currentPage);
        }
      }
    }
  };

  const updateCurrentItem = (id: number | string, item: any) => {
    if (
      getMounted() &&
      navigationCurrentIndex !== undefined &&
      ((typeof id === "number" && id === drawerItems[navigationCurrentIndex].id) ||
        (typeof id === "string" && drawerItems[navigationCurrentIndex].guidId
          ? id === drawerItems[navigationCurrentIndex].guidId
          : id === drawerItems[navigationCurrentIndex].id))
    ) {
      const updatedItems = [...drawerItems];
      updatedItems[navigationCurrentIndex] = item;
      setDrawerItems(updatedItems);
      setCurrentItem(item);
    }
  };

  const setParentListReloadState = () => {
    setListToBeUpdated(true);
  };

  return (
    <DrawerRight ref={ref} onOpen={onDrawerOpen} onClose={onDrawerClose}>
      <EntityPagination
        total={total}
        onCurrentIndexUpdate={updateCurrent}
        initialIndex={indexToOpen ?? 0}
        navigationText={entityText}
        initialPage={filter.page}
        numPerPage={filter.numPerPage}
      />
      {
        <DrawerEntityView
          cardHeader={cardHeader}
          render={!loading ? render : () => <LoadingWithLogo />}
          item={!loading ? currentItem : undefined}
          reloadDrawerItem={updateCurrentItem}
          removeDrawerItem={removeCurrentItem}
          reloadList={setParentListReloadState}
        ></DrawerEntityView>
      }
    </DrawerRight>
  );
};

export default forwardRef(EntityDrawer);
